import { useMemo } from 'react';

/**
 * Parse text into segments of plain text and citation markers.
 *
 * Splits on [n] patterns (e.g. [1], [2], [13], [100]) and returns an array
 * of { type: 'text', value } or { type: 'citation', number } objects.
 *
 * @param {string} text - The text to parse
 * @returns {{ segments: Array<{type: 'text', value: string} | {type: 'citation', number: number}>, citationNumbers: number[] }}
 */
export const parseCitations = (text) => {
  if (!text) return { segments: [], citationNumbers: [] };

  const segments = [];
  const citationNumbers = new Set();

  // Split on citation markers [n] where n is 1-999 (matches rehypeCitation pattern)
  const parts = text.split(/(\[\d{1,3}\])/g);

  for (const part of parts) {
    if (!part) continue;

    const match = part.match(/^\[(\d{1,3})\]$/);
    if (match) {
      const num = parseInt(match[1], 10);
      segments.push({ type: 'citation', number: num });
      citationNumbers.add(num);
    } else {
      segments.push({ type: 'text', value: part });
    }
  }

  return {
    segments,
    citationNumbers: Array.from(citationNumbers).sort((a, b) => a - b),
  };
};

/**
 * Strip citation markers [n] from text (for clipboard copy).
 *
 * @param {string} text
 * @returns {string}
 */
export const stripCitations = (text) => {
  if (!text) return '';
  return text
    .replace(/\[¶\d{1,6}(?:-¶\d{1,6})?\]/g, '')   // strip Article Chat block citations [¶N] and ranges [¶N-¶M]
    .replace(/\[\d{1,3}\]/g, '')                    // strip Lib Chat source citations [N]
    .replace(/  +/g, ' ')
    .trim();
};

/**
 * React hook: memoized citation parsing for a message + sources.
 *
 * @param {string} content - Message text
 * @param {Array} sources - Source objects with source_number
 * @returns {{ segments, citationNumbers, getSource: (n: number) => object|null }}
 */
export const useCitationParser = (content, sources = []) => {
  return useMemo(() => {
    const { segments, citationNumbers } = parseCitations(content);

    // Build a lookup map: source_number → source object
    const sourceMap = new Map();
    for (const source of sources) {
      if (source.source_number != null) {
        sourceMap.set(source.source_number, source);
      }
    }

    const getSource = (n) => sourceMap.get(n) || null;

    return { segments, citationNumbers, getSource };
  }, [content, sources]);
};
